import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList } from "vue";
import { computed, ref } from "vue";
import usersService from "@/services/usersService";
import { tryParseApiErrors } from "@/utils";
export default /*@__PURE__*/ _defineComponent({
    __name: 'InviteProviderDialog',
    props: { modelValue: { type: Boolean, default: false } },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const emit = __emit;
        const step = ref("npi");
        const npi = ref("");
        const firstName = ref("");
        const lastName = ref("");
        const fax = ref("");
        const email = ref("");
        const registryFound = ref(false);
        const registryLine = ref("");
        const alreadyActive = ref(false);
        const sentTo = ref("");
        const loading = ref(false);
        const submitClicked = ref(false);
        const errors = ref([]);
        const npiDigits = computed(() => npi.value.replace(/[^0-9]/g, ""));
        const npiError = computed(() => (npi.value && npiDigits.value.length !== 10 ? "An NPI is exactly 10 digits" : ""));
        const registrySummary = computed(() => registryLine.value);
        async function lookup() {
            if (npiDigits.value.length !== 10) {
                errors.value = ["Enter a valid 10-digit NPI."];
                return;
            }
            loading.value = true;
            errors.value = [];
            try {
                const info = await usersService.lookupProviderInvite(npiDigits.value);
                registryFound.value = Boolean(info.found);
                alreadyActive.value = Boolean(info.alreadyActive);
                firstName.value = info.firstName || "";
                lastName.value = info.lastName || "";
                fax.value = info.fax || "";
                registryLine.value = [
                    [info.firstName, info.lastName].filter(Boolean).join(" ") + (info.credential ? `, ${info.credential}` : ""),
                    info.specialty,
                    [info.city, info.state].filter(Boolean).join(", "),
                ]
                    .filter(Boolean)
                    .join(" — ");
                step.value = "confirm";
            }
            catch (error) {
                errors.value = tryParseApiErrors(error);
            }
            finally {
                loading.value = false;
            }
        }
        async function send() {
            submitClicked.value = true;
            if (!firstName.value.trim() || !lastName.value.trim() || !email.value.trim())
                return;
            loading.value = true;
            errors.value = [];
            try {
                const result = await usersService.inviteProvider({
                    npi: npiDigits.value,
                    email: email.value.trim(),
                    firstName: firstName.value.trim(),
                    lastName: lastName.value.trim(),
                    fax: fax.value.trim() || null,
                });
                sentTo.value = result.email;
                step.value = "sent";
            }
            catch (error) {
                errors.value = tryParseApiErrors(error);
            }
            finally {
                loading.value = false;
            }
        }
        function reset() {
            step.value = "npi";
            registryFound.value = false;
            alreadyActive.value = false;
            submitClicked.value = false;
            errors.value = [];
            // Clear the identity fields too: "Different NPI" must never carry one provider's
            // email or names into another provider's invite.
            npi.value = "";
            firstName.value = "";
            lastName.value = "";
            fax.value = "";
            email.value = "";
            sentTo.value = "";
        }
        function close() {
            emit("update:modelValue", false);
            reset();
        }
        return (_ctx, _cache) => {
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": __props.modelValue,
                "max-width": "480",
                "onUpdate:modelValue": close
            }, {
                default: _withCtx(() => [
                    _createVNode(_component_v_card, { class: "pa-6" }, {
                        default: _withCtx(() => [
                            _cache[15] || (_cache[15] = _createElementVNode("h2", { class: "text-h6 mb-1" }, "Invite a provider", -1)),
                            (step.value === 'sent')
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createVNode(_component_v_alert, {
                                        type: "success",
                                        variant: "tonal",
                                        class: "mt-3 mb-4"
                                    }, {
                                        default: _withCtx(() => [
                                            _cache[5] || (_cache[5] = _createTextVNode(" Invite sent to ", -1)),
                                            _createElementVNode("strong", null, _toDisplayString(sentTo.value), 1),
                                            _cache[6] || (_cache[6] = _createTextVNode(". The provider sets a password and picks how they want to be reached. ", -1))
                                        ]),
                                        _: 1
                                    }),
                                    _createVNode(_component_v_btn, {
                                        color: "primary",
                                        block: "",
                                        onClick: close
                                    }, {
                                        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                                _createTextVNode("Done", -1)
                                            ]))]),
                                        _: 1
                                    })
                                ], 64))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _cache[14] || (_cache[14] = _createElementVNode("p", { class: "text-body-2 text-medium-emphasis mb-4" }, " The provider gets read-only access to the prior auths filed under their NPI. ", -1)),
                                    _createVNode(_component_v_text_field, {
                                        modelValue: npi.value,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((npi).value = $event)),
                                        label: "Provider NPI",
                                        placeholder: "10 digits",
                                        disabled: step.value === 'confirm',
                                        "error-messages": npiError.value,
                                        class: "mb-2 lh--force-white-background"
                                    }, null, 8, ["modelValue", "disabled", "error-messages"]),
                                    (step.value === 'npi')
                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                            key: 0,
                                            color: "primary",
                                            block: "",
                                            loading: loading.value,
                                            onClick: lookup
                                        }, {
                                            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                                    _createTextVNode("Look up NPI", -1)
                                                ]))]),
                                            _: 1
                                        }, 8, ["loading"]))
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            (alreadyActive.value)
                                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                                    key: 0,
                                                    type: "info",
                                                    variant: "tonal",
                                                    class: "mb-4"
                                                }, {
                                                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                                            _createTextVNode(" This provider already has a login — nothing to send. ", -1)
                                                        ]))]),
                                                    _: 1
                                                }))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                    (registryFound.value)
                                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                                            key: 0,
                                                            type: "success",
                                                            variant: "tonal",
                                                            density: "compact",
                                                            class: "mb-4"
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _cache[10] || (_cache[10] = _createTextVNode(" NPI registry match: ", -1)),
                                                                _createElementVNode("strong", null, _toDisplayString(registrySummary.value), 1)
                                                            ]),
                                                            _: 1
                                                        }))
                                                        : (_openBlock(), _createBlock(_component_v_alert, {
                                                            key: 1,
                                                            type: "warning",
                                                            variant: "tonal",
                                                            density: "compact",
                                                            class: "mb-4"
                                                        }, {
                                                            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                                                    _createTextVNode(" This NPI could not be verified in the NPI registry. Check the number, or try again later. ", -1)
                                                                ]))]),
                                                            _: 1
                                                        })),
                                                    (registryFound.value)
                                                        ? (_openBlock(), _createBlock(_component_v_text_field, {
                                                            key: 2,
                                                            modelValue: firstName.value,
                                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((firstName).value = $event)),
                                                            label: "First Name",
                                                            "error-messages": submitClicked.value && !firstName.value.trim() ? 'Required' : '',
                                                            class: "mb-2 lh--force-white-background"
                                                        }, null, 8, ["modelValue", "error-messages"]))
                                                        : _createCommentVNode("", true),
                                                    (registryFound.value)
                                                        ? (_openBlock(), _createBlock(_component_v_text_field, {
                                                            key: 3,
                                                            modelValue: lastName.value,
                                                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((lastName).value = $event)),
                                                            label: "Last Name",
                                                            "error-messages": submitClicked.value && !lastName.value.trim() ? 'Required' : '',
                                                            class: "mb-2 lh--force-white-background"
                                                        }, null, 8, ["modelValue", "error-messages"]))
                                                        : _createCommentVNode("", true),
                                                    (registryFound.value)
                                                        ? (_openBlock(), _createBlock(_component_v_text_field, {
                                                            key: 4,
                                                            modelValue: fax.value,
                                                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((fax).value = $event)),
                                                            label: "Provider Fax",
                                                            hint: "From the NPI registry — edit if needed.",
                                                            "persistent-hint": "",
                                                            class: "mb-2 lh--force-white-background"
                                                        }, null, 8, ["modelValue"]))
                                                        : _createCommentVNode("", true),
                                                    (registryFound.value)
                                                        ? (_openBlock(), _createBlock(_component_v_text_field, {
                                                            key: 5,
                                                            modelValue: email.value,
                                                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((email).value = $event)),
                                                            label: "Provider Email",
                                                            type: "email",
                                                            "error-messages": submitClicked.value && !email.value.trim() ? 'Required' : '',
                                                            class: "mb-4 lh--force-white-background"
                                                        }, null, 8, ["modelValue", "error-messages"]))
                                                        : _createCommentVNode("", true),
                                                    (registryFound.value)
                                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                                            key: 6,
                                                            color: "primary",
                                                            block: "",
                                                            loading: loading.value,
                                                            class: "mb-2",
                                                            onClick: send
                                                        }, {
                                                            default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                                                    _createTextVNode(" Send invite ", -1)
                                                                ]))]),
                                                            _: 1
                                                        }, 8, ["loading"]))
                                                        : _createCommentVNode("", true)
                                                ], 64)),
                                            _createVNode(_component_v_btn, {
                                                variant: "text",
                                                block: "",
                                                onClick: reset
                                            }, {
                                                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                                        _createTextVNode("Different NPI", -1)
                                                    ]))]),
                                                _: 1
                                            })
                                        ], 64)),
                                    (errors.value.length)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 2,
                                            type: "error",
                                            variant: "tonal",
                                            class: "mt-4"
                                        }, {
                                            default: _withCtx(() => [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err) => {
                                                    return (_openBlock(), _createElementBlock("div", { key: err }, _toDisplayString(err), 1));
                                                }), 128))
                                            ]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true)
                                ], 64))
                        ]),
                        _: 1
                    })
                ]),
                _: 1
            }, 8, ["model-value"]));
        };
    }
});
